<template>
  <div v-loading="loading" class="op-area-hour-panel">
    <p class="mu-legend">
      μ = <b>μ_d</b>(所需车辆 ceil(已订座位/3)) × <b>σ</b>(区域剩车)；U = W_loc × W_time × W_date × μ。σ 为该剩车数下各车边际的均值，按该区域缓冲 K
      分箱，上限内步长 2（含上限），超过后宽度倍增粗档，σ̄ 取档左右端平均（不按上限封死）。本表
      S_eff / D / 剩车X 是当前路线在库口径，X = S_eff − D；发车 = 本线该窗客运发车；发车X = 发车 − D，对照可售余票。σ 仍按区域库存分箱。
    </p>
    <p v-if="missingMuFields" class="mu-stale">
      当前快照没有已订座位 / μ_d / σ。点「立即求解」或重新初始化后会写入这些字段。
    </p>
    <div ref="tableWrap" class="table-wrap">
      <el-table :data="rows" :max-height="bodyHeight" class="no-hover" stripe empty-text="暂无运营层数据，请先选择路线并完成 EV 推导">
        <el-table-column type="expand">
          <template slot-scope="props">
            <div v-if="!(props.row.bins && props.row.bins.length)" class="bin-empty">
              该时段没有剩车分箱。重启求解器并重新求解后，展开行会列出各档 X 区间、均摊 σ、边际 σ、最终 μ 与 evContrib。
            </div>
            <el-table v-else :data="props.row.bins" size="mini" class="no-hover bin-table"
              :row-class-name="binRowClass">
              <el-table-column label="档" prop="binIndex" width="50"></el-table-column>
              <el-table-column label="剩车区间 X" width="120">
                <template slot-scope="scope">
                  [{{ raw(scope.row.intervalMin) }}, {{ raw(scope.row.intervalMax) }}]
                </template>
              </el-table-column>
              <el-table-column label="均摊σ" width="78">
                <template slot-scope="scope">
                  <el-tooltip content="进目标：该档左端与右端车均 σ̄ 的平均" placement="top">
                    <span>{{ formatNum(scope.row.sigma) }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column label="边际σ" width="78">
                <template slot-scope="scope">
                  <el-tooltip content="档左端第 X 辆的边际 σ，仅诊断" placement="top">
                    <span>{{ formatNum(scope.row.sigmaMarginal) }}</span>
                  </el-tooltip>
                </template>
              </el-table-column>
              <el-table-column label="μ_d×σ̄" width="88">
                <template slot-scope="scope">
                  {{ formatNum(scope.row.mu) }}
                </template>
              </el-table-column>
              <el-table-column label="选中" width="60">
                <template slot-scope="scope">
                  <span v-if="scope.row.selected" class="selected-tag">是</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column label="积分 P" width="80">
                <template slot-scope="scope">
                  {{ raw(scope.row.binPoints) }}
                </template>
              </el-table-column>
              <el-table-column label="evCoeff" width="80">
                <template slot-scope="scope">
                  {{ formatNum(scope.row.evCoeff) }}
                </template>
              </el-table-column>
              <el-table-column label="收工贡献" width="90">
                <template slot-scope="scope">
                  {{ formatNum(scope.row.evContrib) }}
                </template>
              </el-table-column>
            </el-table>
          </template>
        </el-table-column>

        <el-table-column label="时段" prop="slotRange" width="130"></el-table-column>

        <el-table-column label="S_eff" prop="supplyOpEffective" width="70">
          <template slot="header">
            <el-tooltip content="有效供给 = 现存量 S_op + 此前累计收工" placement="top">
              <span>S_eff</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="S_op" prop="supplyOp" width="64">
          <template slot="header">
            <el-tooltip content="按当前路线可服务车辆的现存量" placement="top">
              <span>S_op</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="D" prop="demandCars" width="50">
          <template slot="header">
            <el-tooltip content="本路线该小时窗剩余待覆盖需求车数：座位扣已锁行程后，按每车3座向上取整；只写在窗起点（05:00、06:00），半点行应为 0" placement="top">
              <span>D</span>
            </el-tooltip>
          </template>
        </el-table-column>

        <el-table-column label="I*" prop="inventoryTarget" width="50">
          <template slot="header">
            <el-tooltip content="前向库存目标，仅用于调位 cap" placement="top">
              <span>I*</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="剩车X" width="70">
          <template slot="header">
            <el-tooltip content="在库口径 X = S_eff − D；起点能跑本线的停驶车都计入。σ̄ 按区域 K 分箱，不跟本列重新算" placement="top">
              <span>剩车X</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ shortage: leftover(scope.row) != null && leftover(scope.row) < 0 }">
              {{ raw(leftover(scope.row)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="发车" width="60" class-name="w-col">
          <template slot="header">
            <el-tooltip content="本线该小时窗客运发车数（窗起点有值，半点行为 0）。旧快照无此列请重新求解" placement="top">
              <span>发车</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ raw(scope.row.departCars) }}
          </template>
        </el-table-column>
        <el-table-column label="发车X" width="70" class-name="w-col">
          <template slot="header">
            <el-tooltip content="本线口径：该小时窗客运发车 − D" placement="top">
              <span>发车X</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ shortage: departLeftover(scope.row) != null && departLeftover(scope.row) < 0 }">
              {{ raw(departLeftover(scope.row)) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="已订座位" width="88">
          <template slot="header">
            <el-tooltip content="起点区域×时钟小时已订座位（含已派）。μ_d 按所需车辆 ceil(座位/3) 计算" placement="top">
              <span>已订座位</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ raw(seats(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column label="μ_d" width="64" class-name="mu-col">
          <template slot="header">
            <el-tooltip content="已订热度：按所需车辆，1～3 人同为 1 车；2 车=1.0，4 车=1.2，6 车封顶" placement="top">
              <span>μ_d</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(muDemand(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column label="σ" width="64" class-name="mu-col">
          <template slot="header">
            <el-tooltip content="该剩车数下各车边际 σ 的均值，进目标；堆车越多越小，缺车不加溢价" placement="top">
              <span>σ</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(sigma(scope.row)) }}
          </template>
        </el-table-column>
        <el-table-column label="μ" width="88" class-name="mu-col">
          <template slot="header">
            <el-tooltip content="最终 μ = μ_d × σ̄" placement="top">
              <span>μ</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <el-tooltip :content="muTooltip(scope.row)" placement="top">
              <span>{{ formatNum(muFinal(scope.row)) }}</span>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column label="W_loc" width="70" class-name="w-col">
          <template slot="header">
            <el-tooltip content="区域权重 W_loc" placement="top">
              <span>W_loc</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(scope.row.wLoc) }}
          </template>
        </el-table-column>
        <el-table-column label="W_time" width="76" class-name="w-col">
          <template slot="header">
            <el-tooltip content="时段权重 W_time（按该行时钟小时）" placement="top">
              <span>W_time</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(scope.row.wTime) }}
          </template>
        </el-table-column>
        <el-table-column label="W_date" width="76" class-name="w-col">
          <template slot="header">
            <el-tooltip content="日期权重 W_date（指定日期覆盖优先，否则工作日/周末/节假日）" placement="top">
              <span>W_date</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ formatNum(scope.row.wDate) }}
          </template>
        </el-table-column>
        <el-table-column label="收工EV" width="80">
          <template slot-scope="scope">
            {{ formatNum(scope.row.evObjective) }}
          </template>
        </el-table-column>
        <el-table-column label="Σz" width="70">
          <template slot="header">
            <el-tooltip content="本时段收工车数；若与全局不同会显示全局值" placement="top">
              <span>Σz</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            <span :class="{ 'stop-work-hit': displayStopZ(scope.row) > 0 }">
              {{ formatStopZ(scope.row) }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="P" width="70">
          <template slot="header">
            <el-tooltip content="收班有效积分总量" placement="top">
              <span>P</span>
            </el-tooltip>
          </template>
          <template slot-scope="scope">
            {{ raw(scope.row.scoreTotal) }}
          </template>
        </el-table-column>

        <el-table-column label="Σz累计" prop="stopWorkCumPrev" width="76"></el-table-column>

        <el-table-column label="P_rem" width="80">
          <template slot-scope="scope">
            {{ formatPEffList(scope.row.nodeRemainingPoints) }}
          </template>
        </el-table-column>
        <el-table-column label="P_eff" width="80">
          <template slot-scope="scope">
            {{ formatPEffList(scope.row.precomputedPEff) }}
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script>
/** 运营层诊断：主表按 μ=μ_d×σ̄ 展示；展开行为剩车分箱（均摊 σ 进目标）。 */
export default {
  props: {
    rows: {
      type: Array,
      default: () => []
    },
    tableHeight: {
      type: Number,
      default: 480
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      wrapHeight: 0
    };
  },

  computed: {
    bodyHeight() {
      return this.wrapHeight || this.tableHeight;
    },
    missingMuFields() {
      const list = this.rows || [];
      if (!list.length) {
        return false;
      }
      return list.every((row) => row && row.muDemandSeats == null && row.muDemand == null && row.sigma == null);
    }
  },

  watch: {
    missingMuFields() {
      this.$nextTick(() => this.syncWrapHeight());
    }
  },

  mounted() {
    this.$nextTick(() => this.bindWrapObserver());
  },

  beforeDestroy() {
    this.unbindWrapObserver();
  },

  methods: {
    bindWrapObserver() {
      this.syncWrapHeight();
      const wrap = this.$refs.tableWrap;
      if (!wrap || typeof ResizeObserver === "undefined") {
        return;
      }
      this._wrapObserver = new ResizeObserver(() => {
        this.syncWrapHeight();
      });
      this._wrapObserver.observe(wrap);
    },
    unbindWrapObserver() {
      if (this._wrapObserver) {
        this._wrapObserver.disconnect();
        this._wrapObserver = null;
      }
    },
    syncWrapHeight() {
      const wrap = this.$refs.tableWrap;
      if (!wrap) {
        return;
      }
      const h = Math.floor(wrap.clientHeight);
      if (h > 0 && h !== this.wrapHeight) {
        this.wrapHeight = h;
      }
    },
    raw(val) {
      return val === null || val === undefined ? "-" : val;
    },
    formatNum(val) {
      if (val === null || val === undefined) {
        return "-";
      }
      return this.$options.filters.numFormat(val, 2);
    },
    selectedBin(row) {
      const bins = row && row.bins;
      if (!bins || !bins.length) {
        return null;
      }
      return bins.find((b) => b && b.selected) || null;
    },
    leftover(row) {
      if (row && row.supplyOpEffective != null && row.demandCars != null) {
        return row.supplyOpEffective - row.demandCars;
      }
      return row.surplusOpMu;
    },
    departLeftover(row) {
      if (!row) {
        return null;
      }
      if (row.departSurplus != null) {
        return row.departSurplus;
      }
      if (row.departCars != null && row.demandCars != null) {
        return row.departCars - row.demandCars;
      }
      return null;
    },
    seats(row) {
      return row.muDemandSeats;
    },
    muDemand(row) {
      if (row.muDemand != null) {
        return row.muDemand;
      }
      const bin = this.selectedBin(row);
      if (bin && bin.sigma && bin.mu != null) {
        return bin.mu / bin.sigma;
      }
      return null;
    },
    sigma(row) {
      if (row.sigma != null) {
        return row.sigma;
      }
      const bin = this.selectedBin(row);
      return bin && bin.sigma != null ? bin.sigma : null;
    },
    muFinal(row) {
      if (row.mu != null) {
        return row.mu;
      }
      const md = this.muDemand(row);
      const sg = this.sigma(row);
      if (md != null && sg != null) {
        return md * sg;
      }
      const bin = this.selectedBin(row);
      return bin && bin.sigma != null && bin.mu != null ? bin.mu : null;
    },
    muTooltip(row) {
      return `μ_d × σ̄ = ${this.formatNum(this.muDemand(row))} × ${this.formatNum(this.sigma(row))}`;
    },
    binRowClass({ row }) {
      return row.selected ? "bin-selected-row" : "";
    },
    formatPEffList(list) {
      if (!list || !list.length) {
        return "-";
      }
      const uniq = [...new Set(list)].sort((a, b) => a - b);
      return uniq.join(", ");
    },
    displayStopZ(row) {
      const pathZ = row.stopWorkCount == null ? 0 : row.stopWorkCount;
      const globalZ = row.globalStopWorkCount == null ? pathZ : row.globalStopWorkCount;
      return Math.max(pathZ, globalZ);
    },
    formatStopZ(row) {
      const pathZ = row.stopWorkCount;
      const globalZ = row.globalStopWorkCount;
      if (pathZ == null && globalZ == null) {
        return "-";
      }
      if (globalZ != null && pathZ != null && globalZ !== pathZ) {
        return `${pathZ} (全局${globalZ})`;
      }
      return this.raw(pathZ != null ? pathZ : globalZ);
    }
  }
};
</script>

<style lang="less" scoped>
.op-area-hour-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.table-wrap {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.mu-legend {
  flex-shrink: 0;
  margin: 0 0 8px;
  color: #c0c4cc;
  font-size: 12px;
  line-height: 1.4;
}

.mu-stale {
  flex-shrink: 0;
  margin: -4px 0 8px;
  color: #e6a23c;
  font-size: 12px;
  line-height: 1.4;
}

.bin-empty {
  padding: 8px 12px;
  color: #909399;
  font-size: 12px;
}

.shortage {
  color: #f56c6c;
  font-weight: 600;
}

.selected-tag {
  color: #409eff;
  font-weight: 600;
}

.stop-work-hit {
  color: #e6a23c;
  font-weight: 600;
}

::v-deep .mu-col {
  background-color: #f4f9ff;
}

::v-deep .w-col {
  background-color: #f8f4ff;
}

::v-deep .bin-selected-row {
  background-color: #ecf5ff !important;
}
</style>
